import {
  ANDROID_KEYCODE_TO_REMOTE, KEYBOARD_KEY_TO_REMOTE, resolveAction,
  type Action, type InputMode, type RemoteKey,
} from '@multiview/shared';
import { attachGamepads } from './gamepad.ts';

export type ActionHandler = (action: Action) => boolean | void;

/**
 * Single entry point for every input device. A desktop keyboard, an Android TV
 * remote routed through the Kotlin bridge, and the on-screen buttons all end up
 * producing the same `Action`, so behaviour cannot drift between them.
 */
class RemoteInput {
  #mode: InputMode = 'browse';
  #handlers: ActionHandler[] = [];
  #attached = false;

  get mode(): InputMode {
    return this.#mode;
  }

  setMode(mode: InputMode): void {
    this.#mode = mode;
  }

  /** Later handlers run first, so a modal can swallow keys from the view below. */
  subscribe(handler: ActionHandler): () => void {
    this.#handlers.push(handler);
    return () => {
      const i = this.#handlers.indexOf(handler);
      if (i >= 0) this.#handlers.splice(i, 1);
    };
  }

  attach(): () => void {
    if (this.#attached) return () => {};
    this.#attached = true;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey || event.altKey || event.metaKey) return;

      const target = event.target;
      if (target instanceof HTMLElement && isTextEntry(target)) {
        if (!escapesTextEntry(event, target)) return;
      }

      const key = KEYBOARD_KEY_TO_REMOTE[event.key];
      if (!key) return;
      if (this.#dispatchKey(key)) event.preventDefault();
    };

    // Emitted by the Android wrapper for keys a WebView never forwards:
    // BACK, CHANNEL_UP/DOWN, the coloured buttons, media transport.
    const onRemoteKey = (event: Event) => {
      const detail = (event as CustomEvent<{ key?: string; keyCode?: number }>).detail;
      if (!detail) return;
      const key = detail.key
        ? (detail.key as RemoteKey)
        : detail.keyCode !== undefined
          ? ANDROID_KEYCODE_TO_REMOTE[detail.keyCode]
          : undefined;
      if (key) this.#dispatchKey(key);
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('remotekey', onRemoteKey);
    const detachGamepads = attachGamepads((key) => {
      // In a text field Left and Right move the caret, as on a keyboard.
      const active = document.activeElement;
      if ((key === 'Left' || key === 'Right') && active instanceof HTMLElement && isTextEntry(active)) return;
      this.#dispatchKey(key);
    });

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('remotekey', onRemoteKey);
      detachGamepads();
      this.#attached = false;
    };
  }

  #dispatchKey(key: RemoteKey): boolean {
    const action = resolveAction(key, this.#mode);
    if (!action) return false;
    return this.dispatch(action);
  }

  dispatch(action: Action): boolean {
    for (let i = this.#handlers.length - 1; i >= 0; i--) {
      if (this.#handlers[i]!(action) === true) return true;
    }
    return false;
  }
}

/* ------------------------------------------------------- text-entry escape */

const TEXT_INPUT_TYPES = new Set(['text', 'search', 'url', 'tel', 'email', 'password', 'number']);

function isTextEntry(el: HTMLElement): boolean {
  if (el.isContentEditable) return true;
  if (el instanceof HTMLTextAreaElement) return true;
  if (el instanceof HTMLInputElement) return TEXT_INPUT_TYPES.has(el.type);
  return false;
}

/**
 * A text field must not swallow every key, or it becomes a focus trap: on a
 * remote the D-pad is the only way out, and there is no Tab key to fall back on.
 *
 * Up and Down always leave. Left and Right move the caret and only leave once it
 * has reached the corresponding end, which is how TV keyboards behave elsewhere.
 */
function escapesTextEntry(event: KeyboardEvent, el: HTMLElement): boolean {
  switch (event.key) {
    case 'Escape':
    case 'ArrowUp':
    case 'ArrowDown':
      return true;

    // Enter in a single-line field means "confirm", and on a remote OK is the
    // only confirm button there is. A textarea keeps it as a newline.
    case 'Enter':
      return el instanceof HTMLInputElement;

    case 'ArrowLeft':
    case 'ArrowRight': {
      if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)) return true;
      const { selectionStart, selectionEnd, value } = el;
      // A number input reports null selection in some engines; treat it as a
      // non-text control so the arrows navigate rather than being swallowed.
      if (selectionStart === null || selectionEnd === null) return true;
      if (selectionStart !== selectionEnd) return false;
      return event.key === 'ArrowLeft' ? selectionStart === 0 : selectionEnd === value.length;
    }

    default:
      return false;
  }
}

export const remote = new RemoteInput();
